import React, { useEffect, useRef, useState } from 'react';
import './VariantCropModal.css';

/**
 * Three-step cropper for an activity image. The whole photo is always shown
 * (contain-scaled to fit the stage); for each required display variant —
 * twoThirds (2:3), square (1:1), half (1:2) and fullWidth (10:3) — the user
 * drags a selection box of that aspect ratio over the photo and resizes it
 * with the slider. Outside the box is dimmed (a huge box-shadow on the box
 * itself), inside is the full-opacity photo. onComplete reports { twoThirds,
 * square, half, fullWidth }, each a crop { x, y, width, height } in pixels of
 * the (already stored) source image — always inside the image bounds, since
 * the box can't leave the stage — which is exactly the ImageVariants body the
 * backend expects. The full image is cropped at display time, so no
 * re-upload is needed.
 *
 * No cropping dependency — plain pointer events + a little math, same
 * approach as AvatarCropModal but with a per-variant box aspect ratio.
 */

const MIN_FRAC = 0.15; // smallest selection, as a fraction of the largest possible box
const MAX_STAGE_W = 960;
const MAX_STAGE_H = 620;

const VARIANTS = [
  { key: 'twoThirds', label: '2/3 de pantalla', aspect: 3 / 2 },
  { key: 'square', label: 'Cuadrada', aspect: 1 },
  { key: 'half', label: '1/2 de pantalla', aspect: 2 / 1 },
  { key: 'fullWidth', label: 'Ancho completo', aspect: 10 / 3 },
];

// Leaves room for the modal's title/description/slider/dots/buttons around
// the stage, so the whole card — sized to the image — still fits the screen.
// The width budget must stay inside the card's own `max-width: 95vw` (minus
// its horizontal padding), otherwise the stage ends up wider than the card
// and spills out of it.
const stageSize = () => ({
  w: typeof window !== 'undefined' ? Math.min(MAX_STAGE_W, window.innerWidth * 0.95 - 48) : MAX_STAGE_W,
  h: typeof window !== 'undefined' ? Math.min(MAX_STAGE_H, window.innerHeight * 0.9 - 300) : MAX_STAGE_H,
});

// Largest box of the given aspect ratio that fits inside a dispW x dispH area.
const maxBoxFor = (aspect, dispW, dispH) => {
  let w = dispW;
  let h = w / aspect;
  if (h > dispH) {
    h = dispH;
    w = h * aspect;
  }
  return { w, h };
};

export default function VariantCropModal({ src, title, onCancel, onComplete, saving }) {
  const [img, setImg] = useState(null); // HTMLImageElement
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [crops, setCrops] = useState({}); // key -> CropRect
  const [frac, setFrac] = useState(1); // selection size, 0..1 of the max box for this aspect
  const [box, setBox] = useState({ x: 0, y: 0 }); // selection top-left, display px
  const drag = useRef(null);

  const variant = VARIANTS[step];
  const stage = stageSize();
  const dispScale = img ? Math.min(stage.w / img.naturalWidth, stage.h / img.naturalHeight) : 1;
  const dispW = img ? img.naturalWidth * dispScale : stage.w;
  const dispH = img ? img.naturalHeight * dispScale : stage.h;
  const maxBox = maxBoxFor(variant.aspect, dispW, dispH);
  const boxW = Math.max(24, maxBox.w * frac);
  const boxH = Math.max(24 / variant.aspect, maxBox.h * frac);

  const clampBox = (pos, w, h) => ({
    x: Math.min(Math.max(0, pos.x), Math.max(0, dispW - w)),
    y: Math.min(Math.max(0, pos.y), Math.max(0, dispH - h)),
  });

  // Decode the source once.
  useEffect(() => {
    let alive = true;
    const image = new Image();
    image.onload = () => alive && setImg(image);
    image.onerror = () =>
      alive && setError('No hemos podido cargar la imagen para recortarla.');
    image.src = src;
    return () => {
      alive = false;
    };
  }, [src]);

  // Reset the selection to fill the frame whenever the image loads or the
  // step (aspect ratio) changes.
  useEffect(() => {
    if (!img) return;
    setFrac(1);
    const mb = maxBoxFor(VARIANTS[step].aspect, dispW, dispH);
    setBox({ x: (dispW - mb.w) / 2, y: (dispH - mb.h) / 2 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, step, dispW, dispH]);

  const onFrac = (e) => {
    const f = Number(e.target.value);
    const nw = Math.max(24, maxBox.w * f);
    const nh = Math.max(24 / variant.aspect, maxBox.h * f);
    setFrac(f);
    setBox((prev) => clampBox({ x: prev.x - (nw - boxW) / 2, y: prev.y - (nh - boxH) / 2 }, nw, nh));
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, ox: box.x, oy: box.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setBox(
      clampBox(
        {
          x: drag.current.ox + (e.clientX - drag.current.startX),
          y: drag.current.oy + (e.clientY - drag.current.startY),
        },
        boxW,
        boxH
      )
    );
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  // Crop rect (in source pixels) currently framed by the selection box —
  // always inside the image bounds, since the box can't leave the stage.
  const currentCrop = () => ({
    x: Math.round(box.x / dispScale),
    y: Math.round(box.y / dispScale),
    width: Math.round(boxW / dispScale),
    height: Math.round(boxH / dispScale),
  });

  const handleNext = () => {
    const next = { ...crops, [variant.key]: currentCrop() };
    setCrops(next);
    if (step < VARIANTS.length - 1) {
      setStep(step + 1);
    } else {
      onComplete(next);
    }
  };

  return (
    <div className="Modal" role="presentation" onMouseDown={saving ? undefined : onCancel}>
      <div
        className="Modal__card VariantCrop"
        role="dialog"
        aria-modal="true"
        aria-label="Recortar variantes"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="Modal__title">{title || 'Ajusta la imagen'}</h2>
        <p className="Modal__desc">
          Variante {step + 1} de {VARIANTS.length}: <strong>{variant.label}</strong>.
          Mueve el recuadro y usa el control para elegir qué parte se verá.
        </p>
        {error && <p className="Modal__error">{error}</p>}

        <div className="VariantCrop__stage">
          <div className="VariantCrop__frame" style={{ width: dispW, height: dispH }}>
            {img && (
              <>
                <img
                  src={img.src}
                  alt=""
                  draggable={false}
                  className="VariantCrop__img"
                  style={{ width: dispW, height: dispH }}
                />
                <div
                  className="VariantCrop__box"
                  style={{ left: box.x, top: box.y, width: boxW, height: boxH }}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                />
              </>
            )}
          </div>
        </div>

        <input
          className="VariantCrop__zoom"
          type="range"
          min={MIN_FRAC}
          max="1"
          step="0.01"
          value={frac}
          onChange={onFrac}
          aria-label="Tamaño de selección"
          disabled={!img}
        />

        <div className="VariantCrop__dots">
          {VARIANTS.map((v, i) => (
            <span
              key={v.key}
              className={`VariantCrop__dot${i === step ? ' is-active' : ''}${
                i < step ? ' is-done' : ''
              }`}
            />
          ))}
        </div>

        <div className="Modal__actions">
          <button type="button" className="Btn Btn--soft" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button
            type="button"
            className="Btn Btn--primary"
            onClick={handleNext}
            disabled={!img || saving}
          >
            {saving
              ? 'Guardando…'
              : step < VARIANTS.length - 1
              ? 'Siguiente →'
              : 'Guardar variantes'}
          </button>
        </div>
      </div>
    </div>
  );
}
